"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "@/i18n/client";

const CONSENT_KEY = "codiaj-cookies-consent-v2";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    analytics: false,
    marketing: false,
    timestamp: new Date().toISOString(),
  });

  const { t } = useTranslations();
  const cookies = t("cookies");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) {
        setShowBanner(true);
      } else {
        setConsent(JSON.parse(stored));
      }
    } catch {
      // Storage unavailable
    }
  }, []);

  function saveConsent(newState: Partial<ConsentState>) {
    const finalState = {
      ...consent,
      ...newState,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(finalState));
      // Force a custom event so layout can listen and update scripts
      window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: finalState }));
    } catch {
      // ignore
    }
    setConsent(finalState);
    setShowBanner(false);
    setShowPreferences(false);
  }

  function acceptAll() {
    saveConsent({ analytics: true, marketing: true });
  }

  function rejectAll() {
    saveConsent({ analytics: false, marketing: false });
  }

  return (
    <>
      {/* 1. Main Banner */}
      {showBanner && !showPreferences && (
        <div
          role="dialog"
          aria-label={cookies.ariaLabel}
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-[color:var(--color-border)] bg-[color:var(--color-bg)] shadow-[0_-4px_32px_rgba(27,22,18,0.08)]"
        >
          <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-5 flex flex-col md:flex-row items-start md:items-center gap-5">
            <p className="font-sans text-[14px] leading-[1.6] text-[color:var(--color-ink)] m-0 flex-grow">
              {cookies.message}{" "}
              <Link
                href="/politica-de-cookies"
                className="text-[color:var(--color-accent)] underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                {cookies.linkLabel}
              </Link>
            </p>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={() => setShowPreferences(true)}
                className="text-[11px] font-mono tracking-widest uppercase text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] transition-colors px-2 py-1"
                aria-label={cookies.ariaConfigure}
              >
                {cookies.configure}
              </button>
              <button
                onClick={rejectAll}
                className="inline-flex items-center justify-center rounded-full border border-[color:var(--color-border)] bg-transparent text-[color:var(--color-ink)] font-mono text-[11px] tracking-[0.14em] uppercase px-[20px] py-[12px] transition-colors hover:border-[color:var(--color-ink)]"
              >
                {cookies.reject}
              </button>
              <button
                onClick={acceptAll}
                className="inline-flex items-center justify-center rounded-full border border-transparent bg-[var(--color-accent)] text-[var(--color-bg)] font-mono text-[11px] tracking-[0.14em] uppercase px-[20px] py-[12px] transition-opacity hover:opacity-90"
              >
                {cookies.accept}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Preferences Panel */}
      {showPreferences && (
        <div className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="bg-[color:var(--color-bg)] w-full max-w-[500px] rounded-2xl shadow-2xl overflow-hidden border border-[color:var(--color-border)]">
            <div className="p-6 sm:p-8">
              <h2 className="font-sans font-bold text-2xl mb-2 text-[color:var(--color-ink)]">
                {cookies.settings}
              </h2>
              <p className="text-sm text-[color:var(--color-ink-muted)] mb-8">
                Personaliza qué cookies permites. Las necesarias siempre están activas.
              </p>

              <div className="space-y-6">
                {/* Necessary */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-[color:var(--color-ink)] mb-1">
                      {cookies.categories.necessary.title}
                    </h3>
                    <p className="text-xs text-[color:var(--color-ink-muted)] leading-relaxed">
                      {cookies.categories.necessary.desc}
                    </p>
                  </div>
                  <div className="h-5 w-10 rounded-full bg-[color:var(--color-accent)] opacity-50 relative">
                    <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" />
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-[color:var(--color-ink)] mb-1">
                      {cookies.categories.analytics.title}
                    </h3>
                    <p className="text-xs text-[color:var(--color-ink-muted)] leading-relaxed">
                      {cookies.categories.analytics.desc}
                    </p>
                  </div>
                  <button
                    onClick={() => setConsent(c => ({ ...c, analytics: !c.analytics }))}
                    className={`h-5 w-10 rounded-full transition-colors relative flex items-center ${
                      consent.analytics ? "bg-[color:var(--color-accent)]" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-3 h-3 bg-white rounded-full transition-transform duration-200 shadow-sm ${
                        consent.analytics ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-[color:var(--color-ink)] mb-1">
                      {cookies.categories.marketing.title}
                    </h3>
                    <p className="text-xs text-[color:var(--color-ink-muted)] leading-relaxed">
                      {cookies.categories.marketing.desc}
                    </p>
                  </div>
                  <button
                    onClick={() => setConsent(c => ({ ...c, marketing: !c.marketing }))}
                    className={`h-5 w-10 rounded-full transition-colors relative flex items-center ${
                      consent.marketing ? "bg-[color:var(--color-accent)]" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-3 h-3 bg-white rounded-full transition-transform duration-200 shadow-sm ${
                        consent.marketing ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-10 flex gap-3">
                <button
                  onClick={rejectAll}
                  className="flex-1 py-3 text-[11px] font-mono tracking-widest uppercase border border-[color:var(--color-border)] rounded-full hover:border-[color:var(--color-ink)] transition-colors"
                >
                  {cookies.reject}
                </button>
                <button
                  onClick={() => saveConsent(consent)}
                  className="flex-1 py-3 text-[11px] font-mono tracking-widest uppercase bg-[color:var(--color-ink)] text-[color:var(--color-bg)] rounded-full hover:opacity-90 transition-opacity"
                >
                  {cookies.save}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Persistent Toggle (Bottom-Left) */}
      {!showBanner && !showPreferences && (
        <button
          onClick={() => setShowPreferences(true)}
          className="fixed left-6 bottom-6 z-40 w-12 h-12 bg-[color:var(--color-bg)] border border-[color:var(--color-border)] rounded-full shadow-lg flex items-center justify-center hover:border-[color:var(--color-accent)] transition-all group"
          aria-label={cookies.settings}
        >
          <svg
            className="w-5 h-5 text-[color:var(--color-ink-muted)] group-hover:text-[color:var(--color-accent)] transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button>
      )}
    </>
  );
}
